import { environment } from './environment';

const STORAGE_KEY = 'welby.previewBackend';
const PARAM = 'backend';
const PR_TAG = /^pr-\d{1,6}$/;

/** `pr-<N>` maps to the Cloud Run tag template; anything else must be an own allowlist entry (never `constructor`, `__proto__`…). */
export const resolveBackend = (backends: Record<string, string>, name: string, tagUrl?: string): string | undefined => {
  if (PR_TAG.test(name)) {
    return tagUrl?.replace('{tag}', name);
  }
  const url = Object.prototype.hasOwnProperty.call(backends, name) ? backends[name] : undefined;
  return typeof url === 'string' ? url : undefined;
};

const readStored = (): string | null => {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStored = (name: string | null): void => {
  try {
    if (name) {
      sessionStorage.setItem(STORAGE_KEY, name);
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage blocked: the override then only lasts while ?backend= stays in the URL.
  }
};

/** Fixed badge so nobody mistakes a preview on a local backend for welby-dev. */
const showBadge = (name: string, url: string): void => {
  const badge = document.createElement('div');
  badge.setAttribute('role', 'status');
  badge.style.cssText =
    'position:fixed;left:12px;bottom:12px;z-index:100000;padding:6px 10px;border-radius:6px;' +
    'background:#b45309;color:#fff;font:600 12px/1.4 sans-serif;box-shadow:0 2px 6px rgba(0,0,0,.3)';
  badge.textContent = `Backend: ${name} (${new URL(url).host}) · `;

  const reset = document.createElement('a');
  reset.textContent = 'back to dev';
  reset.href = `${window.location.pathname}?${PARAM}=dev`;
  reset.style.cssText = 'color:#fff;text-decoration:underline';
  badge.appendChild(reset);

  document.body.appendChild(badge);
};

/** Points welbyEndpoint at an allowlisted preview backend chosen with `?backend=<name>` (PR previews only). */
export const applyBackendOverride = (search = window.location.search): void => {
  if (!environment.allowBackendOverride) {
    return;
  }
  const backends = environment.previewBackends ?? {};
  const tagUrl = environment.previewApiTagUrl;

  const requested = new URLSearchParams(search).get(PARAM);
  if (requested === 'dev') {
    writeStored(null);
  } else if (requested) {
    if (resolveBackend(backends, requested, tagUrl)) {
      writeStored(requested);
    } else {
      console.warn(`[backend-override] Unknown backend "${requested}". Allowed: pr-<N>, ${Object.keys(backends).join(', ')}`);
    }
  }

  const name = readStored();
  const url = name ? resolveBackend(backends, name, tagUrl) : undefined;
  if (!name || !url) {
    return;
  }
  environment.welbyEndpoint = url;
  console.warn(`[backend-override] welbyEndpoint → ${url}`);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => showBadge(name, url));
  } else {
    showBadge(name, url);
  }
};

applyBackendOverride();
